import React, { useEffect, useRef, useState } from 'react';
import QuizContent from '../shared/QuizContent';
import GoogleIcon from '../../assets/google';
import {
  createPreLanderAccount,
  getGoogleAuthUrl,
  createModelParams,
  convertSelectionsToApiFormat,
  collectAndStoreUTMData,
  submitEmailOnly,
  getExternalClickIds,
} from '../../utils/api'; // Use main API service
import { getEfTransactionId } from '../../utils/everflow';
import { useQuizSelections } from '../../hooks/useLocalStorage';
import TermsOfService from './termsOfService';
import PrivacyPolicy from './privacyPolicy';
import {
  useFormValidation,
  validationRules,
} from '../../hooks/useFormValidation';
import {
  buildPaywallUrl,
  buildOAuthRedirectPath,
  isFlatRebillArm,
  isPopupFirst,
  isDuelQuiz,
  popupTypeLabel,
} from '../../utils/paywall';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGBValue } from '../../hooks/useGBValue';
import { useGrowthBookValues } from '../../contexts/GrowthBookContext';
import {
  trackRegistrationStarted,
  trackLanderRegistrationCompleted,
  trackLoginCompleted,
  trackOrderCreated,
} from '../../utils/analytics';
import {
  trackRegistrationStartedMixpanel,
  trackRegistrationCompletedMixpanel,
  trackOrderCreatedMixpanel,
  getUserIdFromAccessToken,
} from '../../utils/mixpanel';
import {
  trackServerRegistrationStarted,
  trackServerOrderCreated,
  getUtmAndEf,
  getRegistrationAttribution,
} from '../../utils/serverGtm';
import CountdownTimer from '../shared/CountdownTimer';
import OtpStep from './otpStep';
import {
  PaywallPopup,
  startUpgateCheckout,
  isAuthCheckoutError,
} from '../paywall-popup';
import {
  saveLpSession,
  readLpSession,
  clearLpSession,
  markOauthReturnHandled,
  wasOauthReturnHandled,
} from '../../utils/session';

const Step14 = ({ onContinue, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpState, setOtpState] = useState(null);
  // Paywall-popup flow: when GB type === 'paywall_popup' we DON'T redirect after
  // signup — we hold the registration tokens here and render the popup in place.
  const [popupState, setPopupState] = useState(null);
  // Popup-first (order === 'popup_first'): the paywall popup is shown BEFORE
  // registration. selectedPlan/checkoutOpts hold the plan the anonymous user
  // picked; the form is revealed once one is chosen (or once plans fail to load).
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkoutOpts, setCheckoutOpts] = useState(null);
  // Set when the pre-registration popup can't load plans — we then skip the popup
  // and let registration fall through to the platform paywall (no permanent wall).
  const [plansUnavailable, setPlansUnavailable] = useState(false);
  // Set after a SUCCESSFUL registration whose checkout call then failed: we hold
  // the auth token and re-show the popup so the user can retry Continue without
  // re-registering. order_created has already fired (orderTrackedRef), so the
  // retry popup delegates via onPlanSelected and never double-tracks.
  const [pendingCheckout, setPendingCheckout] = useState(null);
  // order_created is fired from here (not the popup) in popup-first, so it stays
  // one-shot across the phase flip / checkout retries.
  const orderTrackedRef = useRef(false);
  // External click-ids (gclid/fbclid/twclid/ttclid) resolved once, then threaded
  // onto the sGTM registration-started / order-created events so they carry the
  // same ad-attribution the platform's backend adds to its GTM events.
  const [clickIds, setClickIds] = useState(null);
  const location = useLocation();
  const [selections] = useQuizSelections(location.pathname);

  const googleBtnRef = useRef(null);

  // --- Returning visitor -----------------------------------------------------
  // Read ONCE at mount (a fresh page load is exactly the case this exists for):
  // whoever registered in the last hour left a session behind (utils/session.js).
  // With an authToken it is resumable — the popup arm can check out again without
  // re-registering. Without one (the redirect arm spends its token on the
  // platform, or a resumed checkout 401'd) it only carries the email, which
  // prefills the form so the user isn't retyping it.
  const storedSessionRef = useRef(readLpSession());

  // Use the custom validation hook
  const { values, validateAll, getFieldProps } = useFormValidation(
    { email: storedSessionRef.current?.email || '', password: '' },
    {
      email: validationRules.email,
      password: validationRules.password,
    },
  );

  // useEffect(() => {
  //   const setupGoogleAuth = async () => {
  //     if (googleBtnRef.current) {
  //       try {
  //         const googleAuthUrl = await getGoogleAuthUrl(selections);
  //         googleBtnRef.current.href = googleAuthUrl;
  //       } catch (error) {}
  //     }
  //   };

  //   setupGoogleAuth();
  // }, [selections]);
  const ab_testing = useGBValue();
  const { paywallPopupVideo, paywallOrder, fullRebill, locationPriceMultiplier } =
    useGrowthBookValues();
  const postSignupRedirect =
    ab_testing['createsexfriends-post-signup-redirect'];
  const paywallNavType = ab_testing['createsexfriend-paywall-nav-config'];
  const isMagicLink = ab_testing['createsexfriends-magicLink'] === 'ON';
  // Popup-first arm: the popup renders BEFORE registration. Derived (never
  // latched in state) because GrowthBook resolves async — the nav config is
  // 'paywall' on the first render and only becomes 'paywall_popup' once GB (or
  // the sticky sessionStorage snapshot) resolves.
  const isPopupFirstArm =
    paywallNavType === 'paywall_popup' && isPopupFirst(paywallOrder);
  // The paywall-type analytics label — 'paywall_popup_first' for this arm, so the
  // A/B cohort separates in GTM/Mixpanel (backend nav `type` stays 'paywall_popup').
  const popupPaywallType = popupTypeLabel(paywallOrder);
  // Returning visitor's still-spendable token (popup arm only — the redirect arm's
  // is consumed by the platform on arrival). Drives the resume effect below, and
  // suppresses the pre-registration plan phase: without that the plan-picking
  // popup would render for the frame before the effect swaps in the token-backed
  // one, and a Continue in that frame would ask for a plan instead of checking out.
  const resumableToken =
    paywallNavType === 'paywall_popup'
      ? storedSessionRef.current?.authToken
      : undefined;
  // Show the pre-registration popup until the user picks a plan, plans fail to
  // load, or we've already advanced to a checkout/retry state.
  const showPlanPhase =
    isPopupFirstArm &&
    !resumableToken &&
    !selectedPlan &&
    !plansUnavailable &&
    !popupState &&
    !pendingCheckout;

  // --- Popup-first helpers ---------------------------------------------------
  // The chosen plan must survive the Google OAuth round-trip, which destroys
  // React state. Persist it (with a TTL so a stale plan from an earlier attempt
  // in the same tab isn't silently checked out) and read it back on return.
  const PLAN_STORAGE_KEY = 'csf_popup_first_plan';
  const PLAN_TTL_MS = 30 * 60 * 1000;
  // The GrowthBook ab_testing map as resolved BEFORE the Google OAuth hop — see
  // the onclick handler below for why the return leg can't read it live.
  const AB_TESTING_STORAGE_KEY = 'csf_ab_testing';

  // One-shot read of the pre-OAuth ab_testing snapshot. Returns null when there's
  // none (cross-browser OAuth return: no storage) so callers fall back to the live
  // map. Memoized in a ref: the storage entry is cleared on first read, so without
  // this a second read (StrictMode's double-invoked effect) would come back empty
  // and overwrite the good profile properties with GB defaults.
  const storedAbTestingRef = useRef(undefined);
  const takeStoredAbTesting = () => {
    if (storedAbTestingRef.current !== undefined) return storedAbTestingRef.current;
    let snapshot = null;
    try {
      const raw = localStorage.getItem(AB_TESTING_STORAGE_KEY);
      localStorage.removeItem(AB_TESTING_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length) {
        snapshot = parsed;
      }
    } catch (e) {
      // ignore — fall back to the live map
    }
    storedAbTestingRef.current = snapshot;
    return snapshot;
  };

  const persistSelectedPlan = (plan, opts) => {
    try {
      sessionStorage.setItem(
        PLAN_STORAGE_KEY,
        JSON.stringify({ plan, opts, ts: Date.now() }),
      );
    } catch (e) {
      // ignore storage failures
    }
  };

  const readSelectedPlan = () => {
    try {
      const raw = sessionStorage.getItem(PLAN_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.plan || !parsed?.ts) return null;
      if (Date.now() - parsed.ts > PLAN_TTL_MS) {
        sessionStorage.removeItem(PLAN_STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  };

  const clearSelectedPlan = () => {
    try {
      sessionStorage.removeItem(PLAN_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  };

  // Fire order_created (GTM + sGTM + Mixpanel) once. In popup-first this is owned
  // by createAccount (not the popup) so it stays one-shot across the phase flip
  // and any checkout retry. Mirrors the popup's own handleContinue tracking.
  const fireOrderCreated = ({ plan, opts, userId, email }) => {
    if (orderTrackedRef.current || !plan) return;
    orderTrackedRef.current = true;
    const currency = opts?.currencyCode || 'USD';
    const orderItem = {
      item_id: plan._id,
      item_name: `${plan.duration_months} ${
        plan.duration_months > 1 ? 'months' : 'month'
      } subscription`,
      item_category: 'subscriptions',
      price: Number(plan.paymentAmount),
      currency,
    };
    trackOrderCreated({
      plan_id: plan._id,
      value: Number(plan.paymentAmount),
      currency,
      duration_months: plan.duration_months,
      'paywall-type': popupPaywallType,
      'paywall-order': paywallOrder,
    });
    trackServerOrderCreated({
      item: orderItem,
      paywallType: popupPaywallType,
      paywallOrder,
      email,
      clickIds,
    });
    // Mixpanel order_created needs a userId; the Google return path has none
    // (login/OAuth return carries no accessToken to decode) — GTM + sGTM still fire.
    if (userId) {
      trackOrderCreatedMixpanel(
        userId,
        {
          plan_id: plan._id,
          value: Number(plan.paymentAmount),
          currency,
          duration_months: plan.duration_months,
          'paywall-type': popupPaywallType,
          'paywall-order': paywallOrder,
          ...(email ? { email } : {}),
          ...getRegistrationAttribution(),
        },
        // ab_testing carries createsexfriend-paywall-nav-config +
        // createsexfriend-paywall-order — attached to the event + profile so the
        // popup-first order_created is segmentable by the experiment arm.
        ab_testing,
      );
    }
  };

  // Fire the LP-owned registration funnel events for a paywall_popup password /
  // magic-link registration: GTM + sGTM registration_started, and the identified
  // Mixpanel registration_started AND registration_completed (both deduped against
  // the backend/platform copies via $insert_id, so they count once even if the user
  // later reaches the platform). Returns the Mongo _id decoded from the accessToken.
  // Google is handled separately (its Mixpanel started is backend-owned; the LP
  // sends only completed, keyed on the uid from the OAuth return URL).
  const fireRegistrationEvents = ({ accessToken, source, email }) => {
    trackRegistrationStarted({
      'paywall-type': paywallNavType,
      'paywall-order': paywallOrder,
      flow: 'paywall_popup',
      source,
      email,
      ...(clickIds || {}),
      ...getUtmAndEf(),
    });
    trackServerRegistrationStarted({
      flow: 'paywall_popup',
      source,
      paywallType: paywallNavType,
      paywallOrder,
      email,
      clickIds,
      accessToken,
    });
    trackRegistrationStartedMixpanel(
      accessToken,
      {
        auth_type: source,
        'paywall-type': paywallNavType,
        'paywall-order': paywallOrder,
      },
      ab_testing,
      email,
      getRegistrationAttribution(),
    );
    const userId = getUserIdFromAccessToken(accessToken);
    return userId;
  };

  // Track order_created, then hand off to the Upgate hosted checkout with the
  // freshly-minted authToken. Throws on failure so the caller can recover.
  const runCheckout = async ({ authToken, plan, opts, userId, email }) => {
    fireOrderCreated({ plan, opts, userId, email });
    await startUpgateCheckout({
      authToken,
      plan,
      bonusTokens: opts?.bonusTokens,
      currencySymbol: opts?.currencySymbol,
      locale: opts?.locale,
      paywallType: popupPaywallType,
      isFlatRebill: isFlatRebillArm(fullRebill),
      priceMultiplier: locationPriceMultiplier,
    });
  };

  // Popup Continue in popup-first: remember the plan and reveal the form. No
  // event fires here — plan selection is represented by order_created, which is
  // emitted post-registration (co-timed with registration_started) so it always
  // carries a real userId and never sends an anonymous Mixpanel event.
  const handlePlanSelected = (plan, opts) => {
    persistSelectedPlan(plan, opts);
    setSelectedPlan(plan);
    setCheckoutOpts(opts);
  };

  // Plans failed to load — reveal the form and let registration fall through to
  // the platform paywall instead of stranding the user behind an empty popup.
  const handlePlansUnavailable = () => setPlansUnavailable(true);

  // Retry surface after a registration-succeeded-but-checkout-failed: re-check-out
  // with the held authToken (no re-registration). order_created already fired for
  // the ORIGINALLY-selected plan and can't re-fire, so we re-check-out THAT plan
  // (held in pendingCheckout), ignoring whatever the retry popup shows — otherwise
  // the user could be charged for a different plan than order_created recorded.
  const handleRetryCheckout = () => {
    runCheckout({
      authToken: pendingCheckout?.authToken,
      plan: pendingCheckout?.plan,
      opts: pendingCheckout?.opts,
      userId: pendingCheckout?.userId,
      email: pendingCheckout?.email,
    })
      .then(() => clearSelectedPlan())
      .catch((err) =>
        console.error('[popup-first] checkout retry failed', err),
      );
  };

  // --- Returning-visitor resume ----------------------------------------------
  // Reopen the paywall popup with the token the visitor already holds (they
  // registered, went to Upgate, backed out) instead of the signup form. No
  // registration events fire — this is the SAME registration, not a new one.
  // Runs in an effect (not at mount) because GrowthBook resolves async: the nav
  // config is 'paywall' on the first render and only becomes 'paywall_popup' once
  // GB (or the sticky sessionStorage snapshot) resolves.
  const resumeHandledRef = useRef(false);
  useEffect(() => {
    if (resumeHandledRef.current) return;
    // resumableToken is undefined in the redirect arm (token already spent) and
    // until GrowthBook resolves the nav config.
    if (!resumableToken) return;
    const stored = storedSessionRef.current;
    if (popupState || pendingCheckout) return;
    // A Google OAuth return carries a FRESHER token on the URL — its own effect
    // owns that path (and fires the registration events for it).
    if (new URLSearchParams(window.location.search).get('auth_token')) return;
    resumeHandledRef.current = true;
    setPopupState({
      authToken: resumableToken,
      userId: stored?.userId,
      email: stored?.email,
      paywallType: popupPaywallType,
      experiments: ab_testing,
      resumed: true,
    });
  }, [
    resumableToken,
    popupPaywallType,
    ab_testing,
    popupState,
    pendingCheckout,
  ]);

  // Returning from the hosted checkout. A fresh load is covered by the mount-time
  // read + the effect above, but browser Back usually restores this page from the
  // back/forward cache instead: JS never re-runs, so nothing above fires and React
  // state stays frozen exactly as we left it — the submit button stuck on
  // "Creating…", or the signup form that was up when the redirect happened (which
  // is what made a returning user look un-registered). Re-read the session on every
  // return to the page and put the popup back. `pageshow` covers the bfcache
  // restore; visibilitychange covers an in-app browser handing the tab back
  // without one. Keyed on checkoutAt so a dismissed/finished attempt isn't
  // reopened on every alt-tab, while a SECOND trip to Upgate is.
  const resumedCheckoutAtRef = useRef(null);
  useEffect(() => {
    const resume = () => {
      const fresh = readLpSession();
      storedSessionRef.current = fresh;
      if (!fresh?.authToken || !fresh.checkoutAt) return;
      if (paywallNavType !== 'paywall_popup') return;
      if (resumedCheckoutAtRef.current === fresh.checkoutAt) return;
      resumedCheckoutAtRef.current = fresh.checkoutAt;
      resumeHandledRef.current = true;
      setIsLoading(false);
      setPopupState({
        authToken: fresh.authToken,
        userId: fresh.userId,
        email: fresh.email,
        paywallType: popupPaywallType,
        experiments: ab_testing,
        resumed: true,
      });
    };
    const onPageShow = (event) => {
      if (event.persisted) resume();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resume();
    };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [paywallNavType, popupPaywallType, ab_testing]);

  // The stored token is dead — spent by the platform (a completed checkout
  // consumes it at /lobby) or aged out of the backend's 1h Redis window. Drop the
  // token but KEEP the email (the account exists) and fall back to the auth form;
  // anything else (network blip, 5xx) stays retryable on the popup's Continue.
  const handleResumeCheckoutError = (err) => {
    if (!isAuthCheckoutError(err)) return;
    const stored = storedSessionRef.current;
    if (stored?.email) {
      const tokenless = { email: stored.email, source: stored.source };
      saveLpSession(tokenless);
      storedSessionRef.current = tokenless;
    } else {
      clearLpSession();
      storedSessionRef.current = null;
    }
    setPopupState(null);
    // Password users go to the login card — the signup form can only answer
    // "email already used". Google/magic-link stay on this card: its Google button
    // re-authenticates an existing account and its email-only submit recovers one
    // via OTP. The email is already prefilled either way.
    if (!isMagicLink && stored?.source === 'password' && onContinue) onContinue();
  };

  // Plans can't load (outage, adblock, CSP) on a resumed session: Continue would
  // never enable, walling the user in. Spend the stored token on the platform
  // paywall instead — the same destination the redirect arm uses.
  const handleResumePlansUnavailable = async () => {
    const authToken = storedSessionRef.current?.authToken;
    if (!authToken) return;
    let modelParams = null;
    if (selections) {
      try {
        modelParams = await createModelParams(selections);
      } catch (err) {
        // ignore — the paywall still works without model-params
      }
    }
    window.location.href = buildPaywallUrl(
      authToken,
      'login_paywall',
      modelParams,
      selections?.welcome || 'Realistic',
      undefined,
      undefined,
      undefined,
      ab_testing['createsexfriends-paywall-theme'],
      postSignupRedirect,
      paywallNavType,
    );
  };

  // Persist the resolved GB redirect flag so the post-OAuth return handler in
  // App.jsx (which runs before GrowthBook is initialized) can honor it.
  useEffect(() => {
    if (postSignupRedirect) {
      localStorage.setItem('postSignupRedirect', postSignupRedirect);
    }
  }, [postSignupRedirect]);

  // Persist the nav config too — handleAuthCallback runs after the Google OAuth
  // round-trip, before GrowthBook re-initializes, so it reads this from storage.
  useEffect(() => {
    if (paywallNavType) {
      localStorage.setItem('paywallNavType', paywallNavType);
    }
  }, [paywallNavType]);

  // Resolve external click-ids once on mount (reads landing params from
  // sessionStorage). Threaded onto the sGTM events so they carry ad-attribution.
  useEffect(() => {
    getExternalClickIds()
      .then(setClickIds)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const setupGoogleAuth = async () => {
      const utmObj = collectAndStoreUTMData();

      const efTransactionId = getEfTransactionId();
      const externalClickIds = await getExternalClickIds();

      const oauthPath = buildOAuthRedirectPath(
        postSignupRedirect,
        selections,
        ab_testing['createsexfriends-paywall-theme'],
        paywallNavType,
      );
      const finalUtmObj = {
        ...utmObj,
        ab_testing: JSON.stringify(ab_testing),
         lp_source: process.env.REACT_APP_VERCEL_ENV ? 'createsexfriend' : 'swipey.ai/createsexfriend',
        ...(efTransactionId ? { ef_transaction_id: efTransactionId } : {}),
        externalClickIds,
        path: oauthPath,
        ...(paywallNavType === 'paywall_popup'
          ? {
              // Popup-first: carry the chosen plan id on the returnUrl so it
              // round-trips through Google/BE verbatim (BE does `new URL(returnUrl)`
              // then appends auth_token/uid). This makes the plan survive even a
              // cross-browser OAuth completion, where sessionStorage is empty.
              returnUrl: `${window.location.origin}${window.location.pathname}${
                isPopupFirstArm && selectedPlan
                  ? `?csf_plan=${encodeURIComponent(selectedPlan._id)}`
                  : ''
              }`,
            }
          : {}),
      };

      // duel_quiz carries no character — the backend appends state['model-params']
      // to the redirect verbatim, so it must stay unset for this arm.
      if (selections && !isDuelQuiz(paywallNavType)) {
        try {
          const modelParams = await createModelParams(selections);
          finalUtmObj['model-params'] = modelParams;
          finalUtmObj.type =
            selections.welcome === 'Anime' ? 'anime' : 'realistic';
        } catch (err) {
          console.error('Error creating model params for Google auth', err);
        }
      }

      if (googleBtnRef.current) {
        const googleAuthUrl = getGoogleAuthUrl();

        const oauthUrl = `${googleAuthUrl}?state=${encodeURIComponent(
          JSON.stringify(finalUtmObj),
        )}`;
        googleBtnRef.current.href = oauthUrl;
        googleBtnRef.current.onclick = (e) => {
          e.preventDefault();
          // Regular (redirect) Google flow: push dataLayer registration_started at
          // the click. For paywall_popup we DON'T fire it here — a keepalive fetch
          // fired right before window.location.assign races the OAuth navigation and
          // is often dropped, so the identified sGTM copy is emitted reliably on the
          // OAuth RETURN instead (see the auth_token effect). A dataLayer push for
          // popup would also be forwarded to Mixpanel anonymously (the double).
          if (paywallNavType !== 'paywall_popup') {
            trackRegistrationStarted({ 'paywall-type': paywallNavType });
          }
          localStorage.setItem('googleAuthInProgress', 'true');
          localStorage.setItem('googleAuthStartTime', Date.now().toString());

          localStorage.setItem('pendingRegistrationSource', 'google');
          // GrowthBook is dead on the OAuth return leg: the return handler runs on
          // a FRESH page load, before GB has fetched its payload, so the
          // ab_testing it would read is mostly flag DEFAULTS (only the nav config
          // survives, via the sticky sessionStorage snapshot). Persist the
          // resolved map here so the Mixpanel user properties set on return carry
          // the variants the user actually saw. One-shot — cleared on read.
          // Only the popup flow comes back to the LP, so only it needs (and stores)
          // the snapshot.
          if (paywallNavType === 'paywall_popup') {
            try {
              localStorage.setItem(
                AB_TESTING_STORAGE_KEY,
                JSON.stringify(ab_testing),
              );
            } catch (e) {
              // ignore storage failures — the return falls back to the live map
            }
          }
          window.location.assign(oauthUrl);
        };
      }
    };

    setupGoogleAuth();
    // selectedPlan is in the deps so the Google href is rebuilt with ?csf_plan
    // once the user picks a plan (popup-first shows the popup before the form).
  }, [ab_testing, selections, postSignupRedirect, paywallNavType, selectedPlan]);

  useEffect(() => {
    const pendingSource = localStorage.getItem('pendingRegistrationSource');
    const isCompleted = localStorage.getItem('isCompleted');
    // A slow (>3min) Google round-trip sets isCompleted (App.jsx), which would
    // make this fire lander_registration_completed a SECOND time on a popup return
    // — the auth_token effect below already owns that event. Skip it here.
    const isPopupReturn = new URLSearchParams(window.location.search).has(
      'auth_token',
    );

    if (pendingSource === 'google' && isCompleted && !isPopupReturn) {
      trackLanderRegistrationCompleted('google');
      localStorage.removeItem('pendingRegistrationSource');
    }
  }, []);

  // Google popup return: backend sent ?auth_token (+ ?uid, and ?csf_plan for
  // popup-first) here — reopen the popup. Identify the return from the URL itself,
  // NOT the GB-resolved nav type: GB can't re-resolve paywall_popup on a
  // cross-browser / in-app-webview return where sessionStorage/localStorage are
  // empty. The regular (non-popup) Google flow never comes back to the LP, and the
  // login-screen handleAuthCallback needs auth_type (absent here), so keying on the
  // uid the backend rides back (buildPaywallPopupReturnUrl) can't hijack another flow.
  useEffect(() => {
    if (popupState) return;
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get('auth_token');
    if (!authToken) return;
    const uid = params.get('uid');
    const csfPlanId = params.get('csf_plan');
    // `is_new=0` → this Google user ALREADY had an account (abandoned an Upgate
    // checkout and came back, re-clicked the ad, lost the tab's sessionStorage).
    // The backend now returns them to this same popup instead of the platform
    // /paywall, so the flow below is identical — but the registration events must
    // not fire, or every repeat Google login counts as a new registration.
    // Absent marker = new: the only case a backend without this param produces.
    const isReturningUser = params.get('is_new') === '0';
    const isPopupReturn = !!uid || paywallNavType === 'paywall_popup';
    if (!isPopupReturn) return;
    // popup-first is detectable cross-browser by the csf_plan marker (only ever set
    // on a popup-first returnUrl), so autoCheckout + the A/B arm label still work
    // even when GB has reverted to the default arm in a fresh browser context.
    const isPopupFirstReturn = isPopupFirstArm || !!csfPlanId;
    const effectiveOrder = isPopupFirstReturn ? 'popup_first' : paywallOrder;
    const effectivePaywallType = popupTypeLabel(effectiveOrder);

    // The ab_testing map as resolved BEFORE the OAuth hop. GB hasn't fetched its
    // payload yet on this fresh page load, so the live map here is mostly flag
    // DEFAULTS — use the snapshot for everything that reports the user's variants
    // (Mixpanel user properties now, the popup's order_created later).
    const effectiveAbTesting = takeStoredAbTesting() || ab_testing;
    const stripAuthParams = () => {
      try {
        const url = new URL(window.location.href);
        ['auth_token', 'uid', 'csf_plan', 'is_new'].forEach((k) =>
          url.searchParams.delete(k),
        );
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        // ignore
      }
    };

    // Coming BACK to this return URL rather than arriving at it: browser Back from
    // Upgate, a reload, a restore that resurrects the pre-strip URL. Registration
    // already happened and checkout already ran once, so re-running the block
    // below would double-fire the registration events and — the symptom users
    // report — bounce them straight back into Upgate instead of letting them
    // change anything. Reopen the pricing popup in its normal pick-a-plan mode,
    // wired to the same token: Continue goes to checkout with no second
    // registration and no email prompt. `checkoutAt` covers a tab where the
    // session survived but the handled-marker did not.
    const storedSession = readLpSession();
    if (wasOauthReturnHandled(authToken) || storedSession?.checkoutAt) {
      stripAuthParams();
      setPopupState({
        authToken,
        userId: uid,
        paywallType: effectivePaywallType,
        experiments: effectiveAbTesting,
        resumed: true,
      });
      return;
    }
    // Claim the token BEFORE the work below: it ends in a checkout that navigates
    // away, and a Back into this same entry must find it already claimed.
    markOauthReturnHandled(authToken);
    // registration_started emitted HERE on the OAuth return, BEFORE the completed
    // event below (funnel order), not at the click: a keepalive fetch at click races
    // the immediate navigation to Google and is often dropped. dataLayer push (web
    // container) + sGTM (server) copy.
    // A returning Google user did not register — the backend matched an existing
    // account and reissued a checkout token. Report the login instead; the backend
    // fires its own landing_page_login, and Mixpanel's registration_completed is
    // deduped by $insert_id, not by "did this registration actually happen".
    if (isReturningUser) {
      trackLoginCompleted();
    } else {
      trackRegistrationStarted({
        'paywall-type': paywallNavType,
        'paywall-order': effectiveOrder,
        flow: 'paywall_popup',
        source: 'google',
        ...(clickIds || {}),
        ...getUtmAndEf(),
      });
      trackServerRegistrationStarted({
        flow: 'paywall_popup',
        source: 'google',
        paywallType: paywallNavType,
        paywallOrder: effectiveOrder,
        clickIds,
      });
      trackLanderRegistrationCompleted('google');
    }
    // Clear the one-shot Google flags now that this effect owns the completed
    // event, so a later SPA re-nav to a bare URL can't re-fire the isCompleted
    // effect below (which would double-push lander_registration_completed).
    localStorage.removeItem('pendingRegistrationSource');
    localStorage.removeItem('isCompleted');
    // registration_completed for Google — sent from the LP HERE, when the user
    // lands back on the paywall popup after finishing Google OAuth (per requirement).
    // The backend rides the Mongo _id on the return URL as `uid`, so we can send the
    // IDENTIFIED event; it dedupes with the backend's own copy via the shared
    // `${_id}:registration_completed` $insert_id, so it counts once.
    if (uid && !isReturningUser) {
      trackRegistrationCompletedMixpanel(
        uid,
        {
          source: 'google',
          auth_type: 'google',
          'paywall-type': paywallNavType,
          'paywall-order': effectiveOrder,
        },
        effectiveAbTesting,
        // Google returns no email to the LP, so email is omitted; attribution is
        // still available from the stored utm_data snapshot.
        undefined,
        getRegistrationAttribution(),
      );
    }

    // Same reason as the password/magic-link hand-off above: if the user backs out
    // of the Upgate page they land on a fresh LP with no React state, and this is
    // what stops the signup form asking them to register again (utils/session.js).
    saveLpSession({ authToken, userId: uid || undefined, source: 'google' });

    // Popup-first: the user picked a plan before the OAuth hop. Strip auth_token/uid/
    // csf_plan from the URL first so a browser Back from Upgate can't re-run this
    // effect and re-fire checkout, then reopen the popup with that plan preselected.
    if (isPopupFirstReturn) {
      // Prefer the plan id carried on the returnUrl (survives a cross-browser OAuth
      // where sessionStorage is empty); fall back to the sessionStorage copy. No
      // plan → default selection (existing popup behavior).
      stripAuthParams();
      const stored = readSelectedPlan();
      clearSelectedPlan();
      const preselectId = csfPlanId || stored?.plan?._id || undefined;
      setPopupState({
        authToken,
        userId: uid,
        preselectedPlanId: preselectId,
        // Known plan → highlight it and open the Upgate checkout automatically.
        // Unknown plan → show the popup for the user to pick (existing behavior).
        autoCheckout: !!preselectId,
        // Carry the resolved arm so the popup's own order_created is labelled
        // correctly even cross-browser (where popupPaywallType would default).
        paywallType: effectivePaywallType,
        // …and the pre-OAuth variant set, for the same reason.
        experiments: effectiveAbTesting,
      });
      return;
    }

    setPopupState({
      authToken,
      userId: uid,
      paywallType: effectivePaywallType,
      experiments: effectiveAbTesting,
    });
  }, [paywallNavType, popupState, ab_testing, clickIds, isPopupFirstArm, paywallOrder]);

  const handleEmailOnlySubmit = async () => {
    const emailError = validationRules.email(values.email);
    if (emailError) {
      setEmailError(emailError);
      return;
    }
    setEmailError('');
    setIsLoading(true);
    setError('');
    try {
      const data = await submitEmailOnly(values.email, {
        selections,
        ab_testing,
      });

      if (data?.success === false) {
        setEmailError(data.message || 'Email already used');
        return;
      }

      if (data?.otp_sent) {
        setOtpState({ email: values.email, otpExpiry: data.otpExpiry });
        return;
      }

      // Magic-link new email: same popup-in-place path as the password flow.
      // Gated on !plansUnavailable (like the password branch) so a plans outage
      // falls through to the platform paywall below instead of opening a popup
      // that has no onPlansUnavailable handler and would strand the user.
      if (
        data?.success &&
        data?.accessToken &&
        paywallNavType === 'paywall_popup' &&
        !plansUnavailable
      ) {
        // LP registration funnel: GTM + sGTM started, identified Mixpanel started +
        // completed. GTM lander completed fires AFTER (funnel order preserved).
        const mlUserId = fireRegistrationEvents({
          accessToken: data.accessToken,
          source: 'magic_link',
          email: values.email,
        });
        trackLanderRegistrationCompleted('manual_otp', values.email);
        // Survive a round-trip through the Upgate hosted checkout: if the user
        // backs out and comes back, this is what reopens the popup with the token
        // they already hold instead of re-registering them (utils/session.js).
        saveLpSession({
          authToken: data.authToken,
          userId: mlUserId,
          email: values.email,
          source: 'magic_link',
        });

        // Popup-first: the plan was chosen before registration — check out now
        // with the fresh authToken instead of opening the popup.
        if (isPopupFirstArm && selectedPlan) {
          try {
            await runCheckout({
              authToken: data.authToken,
              plan: selectedPlan,
              opts: checkoutOpts,
              userId: mlUserId,
              email: values.email,
            });
            clearSelectedPlan();
            return;
          } catch (err) {
            console.error('[popup-first] magic-link checkout failed', err);
            // Hold the ORIGINAL plan/opts so the retry re-checks-out the same plan
            // order_created already recorded (see handleRetryCheckout).
            setPendingCheckout({
              authToken: data.authToken,
              userId: mlUserId,
              email: values.email,
              plan: selectedPlan,
              opts: checkoutOpts,
            });
            return;
          }
        }

        setPopupState({
          authToken: data.authToken,
          accessToken: data.accessToken,
          // Mongo _id decoded from the prelander JWT — identifies the Mixpanel
          // order_created fired later inside the popup.
          userId: mlUserId,
        });
        return;
      }

      if (data?.redirectUrl || data?.loginUrl) {
        // popup-first plans-outage fallthrough: the branch above was skipped
        // (!plansUnavailable) and no LP registration_started fired yet, so emit the
        // full funnel here before the lander completed so it isn't completed-without-
        // started. Only for a fresh paywall_popup registration (accessToken present).
        if (
          paywallNavType === 'paywall_popup' &&
          plansUnavailable &&
          data?.accessToken
        ) {
          fireRegistrationEvents({
            accessToken: data.accessToken,
            source: 'magic_link',
            email: values.email,
          });
        }
        trackLanderRegistrationCompleted('manual_otp', values.email);
        // Redirect arm: the platform spends the token on arrival, so there's
        // nothing left to resume — record the email only, so a visitor who comes
        // back gets it prefilled instead of retyping it into a form that can only
        // answer "email already used".
        saveLpSession({ email: values.email, source: 'magic_link' });
        localStorage.setItem('isCompleted', true);
        window.location.href = data.redirectUrl || data.loginUrl;
        return;
      }

      setError(data?.message || 'Something went wrong');
    } catch (err) {
      if (err.message?.includes('we cannot register users from your country')) {
        const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL;
        window.location.href = `${SWIPEY_BASE_URL}/blocked-country`;
      } else {
        setError(err.message || 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isMagicLink) {
      await handleEmailOnlySubmit();
      return;
    }

    // Validate all fields
    if (!validateAll()) {
      return;
    }

    // dataLayer registration_started drives client-side GA4/ad tags. REGULAR
    // (redirect) flow pushes it HERE (before account creation). For paywall_popup it
    // is pushed inside the success branch below (before the completed event), so the
    // funnel order is correct — hence the gate here.
    if (paywallNavType !== 'paywall_popup') {
      trackRegistrationStarted({ 'paywall-type': paywallNavType });
    }
    setIsLoading(true);
    setError('');

    try {
      if (selections) {
        const userAnswers = await convertSelectionsToApiFormat(selections);

        Object.entries(userAnswers.attributes).forEach(([key, value]) => {
          console.log(`${key}: ${value}`);
        });
      }

      // Create account with selections for model-params
      const data = await createPreLanderAccount(
        values.email,
        values.password,
        selections,
        ab_testing,
      );

      // Popup flow: open in place (before the redirect below, which would
      // bounce to the platform paywall). accessToken authorizes checkout.
      // plansUnavailable (popup-first only) falls through to the platform paywall.
      if (data.success && paywallNavType === 'paywall_popup' && !plansUnavailable) {
        // LP registration funnel: GTM + sGTM started, identified Mixpanel started +
        // completed. GTM lander completed fires AFTER (funnel order preserved).
        const pwUserId = fireRegistrationEvents({
          accessToken: data.accessToken,
          source: 'password',
          email: values.email,
        });
        trackLanderRegistrationCompleted('manual', values.email);
        // Survive the Upgate round-trip — see the magic-link branch above.
        saveLpSession({
          authToken: data.authToken,
          userId: pwUserId,
          email: values.email,
          source: 'password',
        });

        // Popup-first: the plan was chosen before registration — check out now
        // with the fresh authToken. Loading stays true across both calls (we
        // return without resetting), so the button can't be double-submitted.
        if (isPopupFirstArm && selectedPlan) {
          try {
            await runCheckout({
              authToken: data.authToken,
              plan: selectedPlan,
              opts: checkoutOpts,
              userId: pwUserId,
              email: values.email,
            });
            clearSelectedPlan();
            return;
          } catch (err) {
            console.error('[popup-first] checkout failed', err);
            // Hold the ORIGINAL plan/opts so the retry re-checks-out the same plan
            // order_created already recorded (see handleRetryCheckout).
            setPendingCheckout({
              authToken: data.authToken,
              userId: pwUserId,
              email: values.email,
              plan: selectedPlan,
              opts: checkoutOpts,
            });
            return;
          }
        }

        setPopupState({
          authToken: data.authToken,
          accessToken: data.accessToken,
          // Mongo _id decoded from the prelander JWT — identifies the Mixpanel
          // order_created fired later inside the popup.
          userId: pwUserId,
        });
        return;
      }

      if (data.redirectUrl) {
        // Redirect arm — email only; see the magic-link branch above.
        if (data.success) saveLpSession({ email: values.email, source: 'password' });
        window.location.href = data.redirectUrl;
        return;
      }

      if (data.success) {
        // popup-first plans-unavailable fallthrough: the pre-submit
        // registration_started is gated off for paywall_popup (see the gate above
        // handleSubmit's try) and the popup success branch was skipped because
        // plansUnavailable is set, so no registration_started fired yet. Emit the
        // full LP funnel here BEFORE the lander completed event so it isn't
        // completed-without-started for the plans-outage cohort.
        if (paywallNavType === 'paywall_popup' && plansUnavailable) {
          fireRegistrationEvents({
            accessToken: data.accessToken,
            source: 'password',
            email: values.email,
          });
        }
        trackLanderRegistrationCompleted('manual', values.email);
        // Redirect arm — email only; see the magic-link branch above.
        saveLpSession({ email: values.email, source: 'password' });

        const modelParams = selections
          ? await createModelParams(selections)
          : null;
        const isNewAccount = window.location.pathname.includes('/newaccount');
        const isPw = window.location.pathname.includes('/pw');
        const isHalloween = window.location.pathname.includes('/halloween');

        const paywallUrl = buildPaywallUrl(
          data.authToken,
          'login_paywall',
          modelParams,
          selections?.welcome || 'Realistic',
          isNewAccount && 'paywall-model-02.mp4',
          isPw && '2',
          // ab_testing.pw_v,
          isHalloween && 'vid.mp4',
          // isPw || isNewAccount ? 'default' : null,
          ab_testing['createsexfriends-paywall-theme'],
          postSignupRedirect,
          paywallNavType,
        );
        localStorage.setItem('isCompleted', true);
        window.location.href = paywallUrl;
      } else {
        setEmailError(data.message || 'Email already used');
      }
    } catch (err) {
      if (err.message.includes('we cannot register users from your country')) {
        const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL;
        window.location.href = `${SWIPEY_BASE_URL}/blocked-country`;
      } else {
        setError(err.message || 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptPrivacy = () => {
    setShowPrivacy(false);
  };

  const handleAcceptTerms = () => {
    setShowTerms(false);
  };

  // Get CSS classes for input styling
  const getEmailInputClass = () => {
    const emailProps = getFieldProps('email');
    if (emailProps.hasError) return 'form-input error-input';
    return 'form-input';
  };

  const getPasswordInputClass = () => {
    const passwordProps = getFieldProps('password');
    if (passwordProps.hasError) return 'form-input error-input';
    return 'form-input';
  };

  const handleSignIn = () => {
    if (onContinue) {
      onContinue();
    }
  };

  const { t } = useTranslation();

  const handleOtpVerified = (data) => {
    // The signup-form OTP step is only reached via the backend `otp_sent` response,
    // which is returned EXCLUSIVELY for an already-existing account (an
    // account-recovery login, not a new registration). So this is a login
    // completion — track it as such (mirroring loginAccount.jsx) instead of
    // inflating registration completions with a "completed" that has no started.
    trackLoginCompleted();

    // Known account from here on. The token rides the redirect and is spent on
    // arrival, so store the email only — enough to prefill a returning visitor's
    // form instead of making them retype it (utils/session.js).
    saveLpSession({ email: otpState?.email, source: 'magic_link' });

    // Existing email (no model_params) → default redirect, never the popup.
    localStorage.setItem('isCompleted', true);
    if (data?.redirectUrl || data?.loginUrl) {
      window.location.href = data.redirectUrl || data.loginUrl;
    }
  };

  // Popup-first checkout-retry surface: registration succeeded but the checkout
  // call failed. Re-show the popup so the user retries Continue with the held
  // authToken (no re-registration). onPlanSelected → handleRetryCheckout, which
  // re-checks-out the ORIGINAL plan (order_created already fired for it and won't
  // re-track). preselectedPlanId highlights that same plan so what the user sees
  // matches what will actually be charged.
  if (pendingCheckout) {
    return (
      <PaywallPopup
        videoSrc={paywallPopupVideo || undefined}
        paywallType={popupPaywallType}
        onPlanSelected={handleRetryCheckout}
        preselectedPlanId={pendingCheckout.plan?._id}
        email={pendingCheckout.email}
        clickIds={clickIds}
      />
    );
  }

  if (popupState) {
    return (
      <PaywallPopup
        accessToken={popupState.accessToken}
        authToken={popupState.authToken}
        userId={popupState.userId}
        videoSrc={paywallPopupVideo || undefined}
        // Prefer the arm resolved on the OAuth return (correct even cross-browser);
        // fall back to the GB-derived label for the in-place password/magic-link path.
        paywallType={popupState.paywallType || popupPaywallType}
        preselectedPlanId={popupState.preselectedPlanId}
        autoCheckout={popupState.autoCheckout}
        // A resumed session carries its own email (from storage); the in-place
        // registration paths use whatever was just typed into the form.
        email={popupState.email || values.email}
        clickIds={clickIds}
        // Resumed session only: escape to the platform paywall if plans can't
        // load, and fall back to the auth form if the stored token turns out to be
        // dead. (A post-registration popup keeps its existing behaviour.)
        onPlansUnavailable={
          popupState.resumed ? handleResumePlansUnavailable : undefined
        }
        onCheckoutError={
          popupState.resumed ? handleResumeCheckoutError : undefined
        }
        // ab_testing carries createsexfriend-paywall-nav-config +
        // createsexfriend-paywall-order onto the popup-fired order_created
        // (after_signup + Google popup-first return). popup-first's own
        // order_created is fired by fireOrderCreated above, not this popup.
        // On a Google return the live map is still GB defaults, so prefer the
        // snapshot the return handler restored (popupState.experiments).
        experiments={popupState.experiments || ab_testing}
      />
    );
  }

  if (otpState) {
    return (
      <QuizContent className="account-page">
        <OtpStep
          email={otpState.email}
          selections={selections}
          ab_testing={ab_testing}
          initialOtpExpiry={otpState.otpExpiry}
          onBack={() => setOtpState(null)}
          onVerified={handleOtpVerified}
          variant="signup"
        />
      </QuizContent>
    );
  }

  return (
    <div>
      {/* Popup-first: the paywall renders BEFORE the form. It's a portal sibling
          (createPortal → document.body) rather than an early return so the form
          — and its googleBtnRef — stays mounted and the Google setup effect keeps
          running. The form underneath is made inert while the popup is up. */}
      {showPlanPhase && (
        <PaywallPopup
          videoSrc={paywallPopupVideo || undefined}
          paywallType={popupPaywallType}
          onPlanSelected={handlePlanSelected}
          onPlansUnavailable={handlePlansUnavailable}
          email={values.email}
          clickIds={clickIds}
        />
      )}
      <div
        aria-hidden={showPlanPhase || undefined}
        {...(showPlanPhase ? { inert: '' } : {})}
        style={showPlanPhase ? { pointerEvents: 'none' } : undefined}
      >
      <QuizContent
        className={`account-page${isMagicLink ? ' account-page--magic' : ''}`}
      >
        <div className="account-container">
          <div className="account-form-wrapper">
            <div className="form-header">
              <h2 className="account-title">{t('create_account')}</h2>
              <p className="account-subtitle">{t('she_waiting')}</p>
              <button className="close-button" type="button" onClick={onClose}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="account-form">
              <a ref={googleBtnRef} href="#" className="google-button">
                <GoogleIcon />
                {isMagicLink ? 'Google' : t('google')}
              </a>

              <div className="separator">
                <span>{`${t('or')}`}</span>
              </div>

              <div className="input-wrapper">
                <input
                  placeholder={t('email_label')}
                  type="email"
                  value={getFieldProps('email').value}
                  onChange={getFieldProps('email').onChange}
                  onBlur={getFieldProps('email').onBlur}
                  className={getEmailInputClass()}
                  required
                  aria-invalid={getFieldProps('email').hasError}
                  aria-describedby={
                    getFieldProps('email').hasError
                      ? 'create-email-error'
                      : undefined
                  }
                  autoComplete="email"
                />
              </div>
              {(getFieldProps('email').hasError || emailError) && (
                <div id="create-email-error" className="error-message-input">
                  {getFieldProps('email').error || emailError}
                </div>
              )}
              {!isMagicLink && (
                <div className="cardStep15InputWrap">
                  <div className="input-wrapper">
                    <input
                      placeholder={t('password_label')}
                      type={showPassword ? 'text' : 'password'}
                      value={getFieldProps('password').value}
                      onChange={getFieldProps('password').onChange}
                      onBlur={getFieldProps('password').onBlur}
                      className={getPasswordInputClass()}
                      required
                      minLength={6}
                      aria-invalid={getFieldProps('password').hasError}
                      aria-describedby={
                        getFieldProps('password').hasError
                          ? 'create-password-error'
                          : undefined
                      }
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="cardStep15InputSvg"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                      aria-label={
                        showPassword ? t('hide_password') : t('show_password')
                      }
                    >
                      {showPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z"
                            stroke="#9B9CAA"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z"
                            stroke="#9B9CAA"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421"
                            stroke="#9B9CAA"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  {getFieldProps('password').hasError && (
                    <div
                      id="create-password-error"
                      className="error-message-input"
                    >
                      {getFieldProps('password').error}
                    </div>
                  )}
                </div>
              )}
              {error && <div className="error-message">{error}</div>}

              <button
                type="submit"
                className="submit-button"
                disabled={isLoading}
              >
                {isLoading ? t('creating_account') : t('create_account2')}
              </button>

              <div className="terms-text">
                {t('by_signing_up')}{' '}
                <span onClick={() => setShowTerms(true)} className="terms-link">
                  {t('terms_of')}
                </span>{' '}
                <span
                  onClick={() => setShowPrivacy(true)}
                  className="terms-link"
                >
                  {t('privacy_policy')}
                </span>
              </div>

              {!isMagicLink && (
                <div className="login-link">
                  {t('already_have')}{' '}
                  <button
                    type="button"
                    onClick={handleSignIn}
                    className="link-button"
                  >
                    {t('log_in')} &gt;
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </QuizContent>
      {showPrivacy && (
        <PrivacyPolicy
          isOpen={showPrivacy}
          onClose={() => setShowPrivacy(false)}
          onAccept={handleAcceptPrivacy}
        />
      )}
      {showTerms && (
        <TermsOfService
          isOpen={showTerms}
          onClose={() => setShowTerms(false)}
          onAccept={handleAcceptTerms}
        />
      )}
      </div>
    </div>
  );
};

export default Step14;
